import { useState, useCallback, useEffect } from "react";
import { useMachine } from "@xstate/react";
import { useNavigate } from "../../hooks/useNavigate";
import {
  immersiveWizardMachine,
  WizardStep,
  WizardFormField,
} from "./machines/immersive-wizard.machine";
import { Button } from "@pushpress/pushpress-ui";
import DemoContainer from "./demos/DemoContainer";
import { WizardFormData } from "./types/wizard.types";
import {
  SubdomainStep,
  ColorStep,
  LogoStep,
  ScheduleStep,
  ClassScheduleStep,
  AppointmentTypesStep,
  StaffAppStep,
  MemberAppStep,
  MembershipPlansStep,
  RankTrackingAppStep,
  CommittedClubAppStep,
  PointOfSalesStep,
} from "./steps";
import { createLightGradient } from "./helpers";
import AppointmentPackagesStep from "./steps/AppointmentPackagesStep";
import { useGetClientGymTypeSubType } from "../../apis/clientApi.hooks";
import Logo from "../../assets/img/pp-logo-primary.svg";
import useTailwindBreakpoint from "../../hooks/useTailwindBreakpoint";

const ImmersiveWizard = () => {
  const { isMdScreen } = useTailwindBreakpoint();
  const navigate = useNavigate();
  const [stepValidation, setStepValidation] = useState<Record<string, boolean>>(
    {},
  );
  const [isCompleting, setIsCompleting] = useState(false);
  const [progress, setProgress] = useState(0);
  const { data: clientGymTypeSubType, isLoading: isLoadingGymType } =
    useGetClientGymTypeSubType();

  const [state, send] = useMachine(immersiveWizardMachine);

  useEffect(() => {
    if (clientGymTypeSubType?.parentGymType?.name) {
      send({
        type: "UPDATE_CONTEXT",
        data: { clientGymType: clientGymTypeSubType.parentGymType.name },
      });
    }
  }, [clientGymTypeSubType, send]);

  const { formData } = state.context;
  const currentStepMeta = state.meta[`immersive-wizard-2025.${state.value}`];

  const handleValidationChange = useCallback(
    (stepId: string, isValid: boolean) => {
      setStepValidation((prev) => ({ ...prev, [stepId]: isValid }));
    },
    [],
  );

  const handleContinue = () => {
    send({ type: "CONTINUE" });
  };

  const handleComplete = () => {
    send({ type: "COMPLETE" });
    setIsCompleting(true);
    setProgress(0);

    // Animate progress over 3 seconds
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 2; // Increase by 2% every 60ms (100% in 3 seconds)
      });
    }, 60);

    setTimeout(() => {
      clearInterval(interval);
      setIsCompleting(false);
      setProgress(0);
      navigate("/start?completed=true");
    }, 3000);
  };

  const handleSkip = () => {
    if (
      state.value === WizardStep.RankTrackingApp ||
      state.value === WizardStep.CommittedClubApp
    ) {
      handleComplete();
    } else {
      send({ type: "SKIP" });
    }
  };

  const handlePrev = () => {
    send({ type: "PREV" });
  };

  const handleFormUpdate = (data: Partial<WizardFormData>) => {
    send({ type: "UPDATE_FORM", data });
  };

  const canContinue = () => {
    const requiresValidation = currentStepMeta?.requiresValidation;
    const currentStep = state.value as string;

    if (requiresValidation) {
      return stepValidation[currentStep] === true;
    }

    return true;
  };

  const shouldShowSkip = () => {
    const skippable = currentStepMeta?.skippable;
    const requiresValidation = currentStepMeta?.requiresValidation;

    // Only show skip when step is skippable AND requires validation AND validation is failing
    return skippable && requiresValidation && !canContinue();
  };

  const canGoPrev = () => {
    return state.value !== WizardStep.Subdomain;
  };

  const gradientStyle = createLightGradient(formData[WizardFormField.Color]);

  const currentStepValidationHandler = useCallback(
    (isValid: boolean) => {
      handleValidationChange(state.value as string, isValid);
    },
    [handleValidationChange, state.value],
  );

  const isCompleteStep = () => {
    return (
      state.value === WizardStep.RankTrackingApp ||
      state.value === WizardStep.CommittedClubApp
    );
  };

  if (isLoadingGymType) {
    return (
      <div className="tw-h-screen tw-w-screen tw-flex tw-items-center tw-justify-center">
        <div className="tw-text-center">
          <div className="tw-animate-spin tw-rounded-full tw-h-12 tw-w-12 tw-border-b-2 tw-border-neutral-800 tw-mb-4"></div>
          <p>Loading your gym setup...</p>
        </div>
      </div>
    );
  }

  if (isCompleting) {
    const brandColor = formData[WizardFormField.Color];
    return (
      <div
        className="tw-h-screen tw-w-screen tw-flex tw-items-center tw-justify-center tw-transition-all tw-duration-500"
        style={gradientStyle}
      >
        <div className="tw-text-center tw-mx-auto tw-px-8">
          <div className="tw-mb-8">
            <h2 className="tw-text-3xl tw-font-bold tw-text-gray-800 tw-mb-4">
              Setting up your business...
            </h2>
            <p className="tw-text-lg tw-text-gray-600 tw-mb-8">
              We&apos;re creating your personalized experience
            </p>

            {/* Progress Bar */}
            <div className="tw-w-full tw-bg-white tw-rounded-full tw-h-3 tw-mb-4">
              <div
                className="tw-h-3 tw-rounded-full tw-transition-all tw-duration-300 tw-ease-out"
                style={{
                  width: `${progress}%`,
                  backgroundColor: brandColor,
                }}
              ></div>
            </div>
            <p className="tw-text-sm tw-font-medium tw-text-gray-500">
              {progress}% Complete
            </p>
          </div>
        </div>
      </div>
    );
  }

  const renderCurrentStep = () => {
    const stepProps = {
      formData,
      onFormUpdate: handleFormUpdate,
      onValidationChange: currentStepValidationHandler,
    };

    switch (state.value) {
      case WizardStep.Subdomain:
        return <SubdomainStep {...stepProps} />;
      case WizardStep.Color:
        return <ColorStep {...stepProps} />;
      case WizardStep.Logo:
        return <LogoStep {...stepProps} />;
      case WizardStep.Schedule:
        return <ScheduleStep {...stepProps} />;
      case WizardStep.ClassSchedule:
        return <ClassScheduleStep {...stepProps} />;
      case WizardStep.AppointmentTypes:
        return <AppointmentTypesStep {...stepProps} />;
      case WizardStep.AppointmentPackages:
        return <AppointmentPackagesStep {...stepProps} />;
      case WizardStep.MembershipPlans:
        return <MembershipPlansStep {...stepProps} />;
      case WizardStep.PointOfSales:
        return <PointOfSalesStep {...stepProps} />;
      case WizardStep.StaffApp:
        return <StaffAppStep {...stepProps} />;
      case WizardStep.MemberApp:
        return <MemberAppStep {...stepProps} />;
      case WizardStep.RankTrackingApp:
        return <RankTrackingAppStep {...stepProps} />;
      case WizardStep.CommittedClubApp:
        return <CommittedClubAppStep {...stepProps} />;
      default:
        return (
          <div className="tw-p-4 tw-bg-neutral-800/10 tw-rounded-lg">
            <p>Step content for {String(state.value)} coming soon...</p>
          </div>
        );
    }
  };

  const isWideStep =
    state.value === WizardStep.StaffApp || state.value === WizardStep.MemberApp;

  return (
    <div
      className="tw-h-screen tw-w-full tw-flex tw-transition-all tw-duration-500 tw-overflow-hidden"
      style={gradientStyle}
    >
      <div className="tw-absolute tw-top-8 tw-left-8 tw-z-10">
        <Logo className="tw-h-8 tw-w-auto" />
      </div>

      {/* Left Side - Step Form */}
      <div
        className={`tw-py-8 tw-px-8 md:tw-px-0 tw-flex tw-flex-col tw-overflow-y-auto tw-text-neutral-800
          tw-w-full ${isWideStep ? "md:tw-w-1/2" : "md:tw-w-1/3"}`}
      >
        <div className="tw-w-full tw-mx-auto md:tw-pt-[40%] tw-pt-[20%] tw-p-8 md:tw-pr-0">
          {/* Step Indicator */}
          <div className="tw-mb-8">
            <div className="tw-w-full tw-bg-neutral-800/20 tw-rounded-full tw-h-2">
              <div
                className="tw-bg-neutral-800 tw-h-2 tw-rounded-full tw-transition-all tw-duration-300"
                style={{
                  width: `${((currentStepMeta?.step_number || 1) / (Object.keys(WizardStep).length - 1)) * 100}%`,
                }}
              />
            </div>
          </div>

          {/* Step Content */}
          <div className="tw-mb-4">
            <h1 className="tw-text-4xl tw-font-bold tw-mb-4">
              {currentStepMeta?.title || "Welcome"}
            </h1>
            {currentStepMeta?.description && (
              <p className="tw-text-xl tw-opacity-90">
                {currentStepMeta.description}
              </p>
            )}
          </div>

          {/* Step Form Content - Fixed height container */}
          <div className="tw-mb-8 tw-min-h-[200px]">{renderCurrentStep()}</div>

          {/* Navigation */}
          <div className="tw-flex tw-gap-4 tw-justify-end tw-w-full">
            {canGoPrev() && (
              <Button onClick={handlePrev} variant="ghost">
                Previous
              </Button>
            )}

            <div className="tw-flex tw-gap-2">
              {shouldShowSkip() && (
                <Button onClick={handleSkip} variant="ghost">
                  Skip
                </Button>
              )}

              <Button
                onClick={isCompleteStep() ? handleComplete : handleContinue}
                disabled={!canContinue()}
                variant="dark"
              >
                {isCompleteStep() ? "Complete" : "Continue"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Demo */}
      {isMdScreen && (
        <div
          className={`tw-p-8 tw-h-full ${isWideStep ? "tw-w-1/2" : "tw-w-2/3"}`}
        >
          <DemoContainer
            currentState={state.value as string}
            formData={formData}
          />
        </div>
      )}
    </div>
  );
};

export default ImmersiveWizard;
